import React from 'react';

const Expenses: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600 mt-2">
            The Expenses page in DAS allows you to track and manage all business expenses efficiently. Record various types of expenses, manage expense approvals, track reimbursements, and maintain detailed expense records for accurate financial reporting and budget management.
          </p>
        </div>

        {/* Main Expenses List Image */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-6">
            <img
              src="/images/purchase/expenses/list.png"
              alt="Expenses List Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
          </div>
          <p className="text-gray-600 text-sm">
            <strong>Expenses Overview:</strong> The main interface showing all recorded expenses with detailed information including dates, branches, expense accounts, reference numbers, notes, payment methods, and amounts. Each expense is clearly categorized for easy tracking and analysis.
          </p>
        </div>

        {/* Expenses Overview Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expenses Overview</h2>
          <p className="text-gray-600 mb-4">
            The Expenses page provides a comprehensive view of all business expenses with detailed tracking capabilities. You can manage different types of expenses, from operational costs like parking fees to larger business expenditures, all with proper documentation and approval workflows.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Expense Table Structure</h3>
          <p className="text-gray-600 mb-4">The expenses table displays the following information:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Date:</strong> The date when the expense was incurred</li>
            <li><strong>Branch:</strong> The business branch where the expense was recorded</li>
            <li><strong>Expense Account:</strong> The chart of accounts category (e.g., "Parking", "Travel", "Office Supplies")</li>
            <li><strong>Reference #:</strong> Optional reference number for tracking and documentation</li>
            <li><strong>Notes:</strong> Descriptive notes about the expense (e.g., "Daily Car Parking")</li>
            <li><strong>Paid Through:</strong> Payment method used (e.g., "Petty Cash", "Bank Account", "Credit Card")</li>
            <li><strong>Amount:</strong> Expense amount in the base currency (MMK)</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Common Expense Types</h3>
          <p className="text-gray-600 mb-4">Based on your current data, typical expense categories include:</p>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Operational Expenses:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Parking fees</li>
                <li>Transportation costs</li>
                <li>Utilities</li>
                <li>Communication expenses</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Office Expenses:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Office supplies</li>
                <li>Equipment maintenance</li>
                <li>Software subscriptions</li>
                <li>Professional services</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Travel & Entertainment:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Business travel</li>
                <li>Meals and entertainment</li>
                <li>Accommodation</li>
                <li>Conference expenses</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Creating New Expenses Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Expenses</h2>
          <p className="text-gray-600 mb-4">
            Adding new expenses is straightforward using the <strong>+ New Expense</strong> button in the Expenses page.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">New Expense Process</h3>
          <p className="text-gray-600 mb-4">To create a new expense:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the <strong>+ New Expense</strong> button in the top-right corner</li>
            <li>The "New Expense" form will open with comprehensive fields for expense details:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/purchase/expenses/create-form.png"
              alt="New Expense Form"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>New Expense Form:</strong> Comprehensive form with all necessary fields for recording expenses including branch, date, expense account, amount, payment method, supplier details, and attachments.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Essential Form Fields</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Basic Information:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Branch:</strong> Select the business branch (required)</li>
                <li><strong>Date:</strong> Expense date (required)</li>
                <li><strong>Expense Account:</strong> Chart of accounts category (required)</li>
                <li><strong>Amount:</strong> Expense amount in MMK (required)</li>
                <li><strong>Currency:</strong> Currency selection (defaults to MMK)</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Additional Details:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Reference #:</strong> Optional reference number</li>
                <li><strong>Notes:</strong> Detailed description of the expense</li>
                <li><strong>Paid Through:</strong> Payment method (required)</li>
                <li><strong>Bank Charges:</strong> Additional bank fees if applicable</li>
                <li><strong>Tax:</strong> Applicable tax rates</li>
              </ul>
            </div>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Supplier and Customer Information</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Supplier:</strong> Link expense to a specific supplier with search functionality</li>
            <li><strong>Customer:</strong> Associate expense with a customer (for reimbursable expenses)</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Attachments and Documentation</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Upload File:</strong> Attach receipts, invoices, or supporting documents</li>
            <li><strong>File Limit:</strong> Maximum 5 files, 5MB each</li>
            <li><strong>Supported Formats:</strong> Images, PDFs, and common document formats</li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              <strong>Best Practice:</strong> Always attach receipts or supporting documentation to expenses for proper audit trails and compliance with expense policies.
            </p>
          </div>
        </div>

        {/* Expense Search and Filtering Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense Search and Filtering</h2>
          <p className="text-gray-600 mb-4">
            DAS provides powerful search capabilities to quickly locate specific expenses through both quick search and advanced filtering options.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
          <p className="text-gray-600 mb-4">
            Use the search icon (🔍) located in the top-right corner of the expenses table for instant expense filtering:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Real-time Search:</strong> Start typing to immediately filter expenses as you type</li>
            <li><strong>Multi-field Search:</strong> Searches across expense accounts, notes, reference numbers, and amounts</li>
            <li><strong>Quick Access:</strong> No need to open separate search forms - search directly from the main view</li>
            <li><strong>Clear Results:</strong> Clear search terms to return to full expense list</li>
          </ul>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Quick Tip:</strong> The search icon provides instant filtering for common searches like finding expenses by account type (e.g., "Parking") or searching by specific amounts or notes.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Advanced Search Dialog</h3>
          <p className="text-gray-600 mb-4">
            For more detailed filtering, access the advanced search functionality:
          </p>

          <div className="mb-6">
            <img
              src="/images/purchase/expenses/search.png"
              alt="Expense Search Dialog"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Search Expense Form:</strong> Advanced search dialog with multiple filter options including branch, expense account, date range, payment method, and expense number for precise expense tracking.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Criteria</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Basic Filters:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Branch:</strong> Filter by specific business branch</li>
                <li><strong>Expense Account:</strong> Filter by chart of accounts category</li>
                <li><strong>Date Range:</strong> Specify start and end dates</li>
                <li><strong>Reference #:</strong> Search by reference number</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Advanced Filters:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Paid Through:</strong> Filter by payment method</li>
                <li><strong>Expense Number:</strong> Search by system-generated expense ID</li>
                <li><strong>Amount Range:</strong> Filter by expense amount</li>
                <li><strong>Supplier:</strong> Filter by associated supplier</li>
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Functionality Benefits</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Quick Search Icon Benefits:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Instant filtering without page refreshes</li>
                <li>Search multiple columns simultaneously</li>
                <li>Perfect for quick lookups and verification</li>
                <li>Maintains current page view and context</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Advanced Search Benefits:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Precise filtering with multiple criteria</li>
                <li>Date range and amount filtering</li>
                <li>Ideal for detailed reporting needs</li>
                <li>Complex search combinations</li>
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Use Cases</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Quick Retrieval:</strong> Use search icon to rapidly locate specific expenses from large datasets</li>
            <li><strong>Audit Support:</strong> Easily find expenses for audit and compliance purposes</li>
            <li><strong>Report Generation:</strong> Filter expenses for specific reporting requirements</li>
            <li><strong>Budget Analysis:</strong> Track expenses by category, branch, or time period</li>
            <li><strong>Expense Verification:</strong> Quickly verify specific transactions or amounts</li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>Efficiency Tip:</strong> Combine the quick search icon for immediate results with advanced search for complex filtering needs. This dual approach maximizes your productivity when managing expenses.
            </p>
          </div>
        </div>

        {/* Expense Details and Management Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense Details and Management</h2>
          <p className="text-gray-600 mb-4">
            Each expense record provides detailed information and management capabilities.
          </p>

          <div className="mb-6">
            <img
              src="/images/purchase/expenses/edit-print-btn.png"
              alt="Expense Details Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Expense Details:</strong> Comprehensive expense information including expense details, journal entries, and action buttons for editing, PDF generation, and file attachments.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Detail View Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Expense Information:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Complete expense details display</li>
                <li>Branch and date information</li>
                <li>Expense account and amount</li>
                <li>Payment method and reference</li>
                <li>Tax calculations and totals</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Management Actions:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Edit expense details</li>
                <li>Generate PDF reports</li>
                <li>Attach supporting files</li>
                <li>View expense history</li>
                <li>Process refunds if needed</li>
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons and Menu Options</h3>
          <p className="text-gray-600 mb-4">The expense details page provides several action options:</p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Primary Actions (Always Visible):</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Edit Button:</strong> Opens the edit form with pre-populated expense data</li>
                <li><strong>PDF/Print Button:</strong> Generates printable expense report in PDF format</li>
                <li><strong>More Actions Menu (⋮):</strong> Contains additional options</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Secondary Actions (In Menu):</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li><strong>Refund Expense:</strong> Process partial or full expense refunds</li>
                <li><strong>Delete:</strong> Remove expense record (with confirmation)</li>
                <li><strong>Duplicate:</strong> Create a copy of the expense for similar entries</li>
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Top Panel Quick Actions</h3>
          <p className="text-gray-600 mb-4">The expense details panel header includes convenient access options:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Attach Files Button:</strong> Quick access to upload additional supporting documents</li>
            <li><strong>Expense History Button:</strong> View complete audit trail and change history</li>
            <li><strong>Close Button (×):</strong> Return to the expenses list view</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Journal Entries</h3>
          <p className="text-gray-600 mb-4">The system automatically generates accounting journal entries for each expense:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Expense Account:</strong> Debit entry to the appropriate expense account</li>
            <li><strong>Payment Account:</strong> Credit entry to the payment method account (Petty Cash, Bank, etc.)</li>
            <li><strong>Branch Allocation:</strong> Proper branch assignment for multi-location businesses</li>
            <li><strong>Tax Entries:</strong> Separate entries for tax components if applicable</li>
          </ul>
        </div>

        {/* Editing Expenses Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Editing Expenses</h2>
          <p className="text-gray-600 mb-4">
            Modify existing expense records to correct information or update details using the Edit button from the expense details page.
          </p>

          <div className="mb-6">
            <img
              src="/images/purchase/expenses/edit-form.png"
              alt="Edit Expense Form"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Edit Expense Form:</strong> Pre-populated form showing existing expense data including parking expense for MMK 2,000 paid through Petty Cash, with options to modify all expense details.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">How to Edit an Expense</h3>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>From the expenses list, click on any expense row to open the expense details</li>
            <li>Click the <strong>Edit</strong> button in the action toolbar</li>
            <li>The edit form opens with all current expense information pre-filled</li>
            <li>Modify the necessary fields as required</li>
            <li>Click <strong>Save</strong> to apply changes or <strong>Cancel</strong> to discard</li>
          </ol>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Editable Fields</h3>
          <p className="text-gray-600 mb-4">Most expense fields can be modified:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Basic Information:</strong> Branch, date, expense account, amount</li>
            <li><strong>Payment Details:</strong> Payment method, bank charges, tax information</li>
            <li><strong>References:</strong> Reference numbers, notes, supplier/customer associations</li>
            <li><strong>Attachments:</strong> Add or remove supporting documents</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Edit Considerations</h3>
          <div className="space-y-3 mb-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-800">Data Integrity</p>
              <p className="text-gray-600 text-sm">Changes to expense details automatically update related journal entries and financial reports.</p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <p className="font-semibold text-gray-800">Approval Impact</p>
              <p className="text-gray-600 text-sm">Editing approved expenses may require re-approval depending on your workflow settings.</p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <p className="font-semibold text-gray-800">Audit Trail</p>
              <p className="text-gray-600 text-sm">All changes are tracked in the expense history for audit and compliance purposes.</p>
            </div>
          </div>
        </div>

        {/* PDF/Print Functionality Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">PDF Generation and Printing</h2>
          <p className="text-gray-600 mb-4">
            Generate professional PDF reports for expense documentation, compliance, and record-keeping purposes.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">PDF/Print Features</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>PDF Generation:</strong> Create formatted PDF documents with complete expense details</li>
            <li><strong>Direct Printing:</strong> Print expense reports directly from the system</li>
            <li><strong>Professional Layout:</strong> Well-formatted reports suitable for business documentation</li>
            <li><strong>Complete Information:</strong> Includes all expense details, journal entries, and attachments list</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">How to Generate PDF/Print</h3>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Open the expense details page for the desired expense</li>
            <li>Click the <strong>PDF/Print</strong> button in the action toolbar</li>
            <li>Choose to either download the PDF or print directly</li>
            <li>The system generates a formatted report with all expense information</li>
          </ol>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">PDF Content Includes</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Expense Information:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Expense date and branch</li>
                <li>Expense account and amount</li>
                <li>Payment method and reference</li>
                <li>Tax details and calculations</li>
                <li>Supplier and customer information</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Additional Details:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Journal entry details</li>
                <li>Account and branch allocations</li>
                <li>List of attached documents</li>
                <li>Expense notes and descriptions</li>
                <li>Company branding and formatting</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>Use Cases:</strong> PDF reports are ideal for expense reimbursements, audit documentation, tax filings, and maintaining organized financial records.
            </p>
          </div>
        </div>

        {/* File Attachments Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">File Attachments Management</h2>
          <p className="text-gray-600 mb-4">
            Attach supporting documents to expenses for complete documentation and compliance with expense policies.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Attachment Features</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Quick Access:</strong> Use the "Attach Files" button in the expense details header</li>
            <li><strong>Multiple Files:</strong> Upload up to 5 files per expense</li>
            <li><strong>File Size Limit:</strong> Maximum 5MB per file</li>
            <li><strong>Format Support:</strong> Images (JPG, PNG), PDFs, and common document formats</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">How to Attach Files</h3>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Open the expense details page</li>
            <li>Click the <strong>Attach Files</strong> button in the top panel</li>
            <li>Select files from your device (receipts, invoices, supporting documents)</li>
            <li>Files are automatically uploaded and linked to the expense</li>
            <li>View attached files in the expense details or PDF reports</li>
          </ol>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Document Types to Attach</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Required Documents:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Original receipts or invoices</li>
                <li>Payment confirmations</li>
                <li>Vendor invoices or bills</li>
                <li>Bank transaction records</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Supporting Documents:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Purchase orders or contracts</li>
                <li>Approval emails or forms</li>
                <li>Delivery receipts</li>
                <li>Additional explanation notes</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Best Practice:</strong> Attach all relevant documentation immediately when creating expenses to ensure complete audit trails and faster approval processes.
            </p>
          </div>
        </div>

        {/* Expense History Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense History and Audit Trail</h2>
          <p className="text-gray-600 mb-4">
            Track all changes and activities related to an expense with comprehensive history logging for audit and compliance purposes.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">History Features</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Complete Audit Trail:</strong> Records all changes made to the expense</li>
            <li><strong>User Tracking:</strong> Shows who made each change and when</li>
            <li><strong>Change Details:</strong> Displays what specific fields were modified</li>
            <li><strong>Activity Log:</strong> Tracks all actions including creation, edits, approvals, and refunds</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">How to View Expense History</h3>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Open the expense details page</li>
            <li>Click the <strong>Expense History</strong> button in the top panel</li>
            <li>View chronological list of all activities and changes</li>
            <li>Review details of each modification including user and timestamp</li>
          </ol>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">History Information Includes</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Change Tracking:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Original expense creation</li>
                <li>Field modifications and updates</li>
                <li>File attachments added/removed</li>
                <li>Status changes and approvals</li>
                <li>Refund processing activities</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Audit Information:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>User identification for each action</li>
                <li>Timestamp of all activities</li>
                <li>IP address and location tracking</li>
                <li>Approval workflow progression</li>
                <li>System-generated journal entries</li>
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Compliance and Audit Benefits</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Regulatory Compliance:</strong> Meet audit requirements with complete documentation</li>
            <li><strong>Internal Controls:</strong> Ensure proper expense approval and processing procedures</li>
            <li><strong>Fraud Prevention:</strong> Detect unauthorized changes or suspicious activities</li>
            <li><strong>Process Improvement:</strong> Analyze expense processing patterns for optimization</li>
          </ul>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Expense history cannot be modified or deleted, ensuring data integrity and providing reliable audit trails for compliance and investigation purposes.
            </p>
          </div>
        </div>

        {/* Expense Refunds Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense Refunds</h2>
          <p className="text-gray-600 mb-4">
            Process refunds for expenses when necessary, such as overpayments or returned items.
          </p>

          <div className="mb-6">
            <img
              src="/images/purchase/expenses/refund-form.png"
              alt="Expense Refund Form"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Expense Refund Form:</strong> Dedicated refund interface with branch selection, date, amount specification, payment method, and description fields for processing expense refunds.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Refund Process</h3>
          <p className="text-gray-600 mb-4">To process an expense refund:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Access the expense details page</li>
            <li>Click the "Refund Expense" option from the actions menu</li>
            <li>Fill in the refund form with appropriate details</li>
            <li>Specify the refund amount and receiving account</li>
            <li>Add description explaining the reason for refund</li>
            <li>Attach supporting documentation if required</li>
            <li>Save the refund to process the transaction</li>
          </ol>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Refund Scenarios</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Overpayment:</strong> Refund excess amount paid for services or goods</li>
            <li><strong>Cancelled Services:</strong> Refund for services that were cancelled or not provided</li>
            <li><strong>Returned Items:</strong> Refund for returned purchases or equipment</li>
            <li><strong>Duplicate Payments:</strong> Refund for expenses that were paid multiple times</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Expenses;
